/**
 * Headless Supabase session helpers for the One Up Admin app.
 *
 * Pass 1 deliberately does NOT pull in `@supabase/supabase-js` or
 * wire any UI into auth. This file provides two things:
 *
 *   1. `readSupabaseAccessToken()` — read the current access token
 *      from the Supabase JS client's localStorage entry, if one was
 *      written by a future AuthProvider that imports
 *      `@supabase/supabase-js`. Returns `null` until that happens.
 *
 *   2. `authedFetch()` — `fetch` wrapper that attaches
 *      `Authorization: Bearer <token>` when a Supabase session is
 *      present. Falls back to plain `fetch` otherwise.
 *
 * Required env (Vite) once a real auth flow ships:
 *   - VITE_SUPABASE_URL
 *   - VITE_SUPABASE_ANON_KEY
 *
 * NEVER add SUPABASE_SERVICE_ROLE_KEY to a `VITE_*` env — service-role
 * keys must stay server-side (Worker secrets only).
 *
 * The `sb-<project-ref>-auth-token` localStorage key is the same
 * storage shape `@supabase/supabase-js` uses, so the next Pass that
 * adds the real client can read/write without a migration.
 */

interface SupabaseStoredSession {
  access_token?: unknown;
  refresh_token?: unknown;
  expires_at?: unknown;
}

export function readSupabaseAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  const url = readEnv('VITE_SUPABASE_URL');
  if (!url) return null;
  const projectRef = extractProjectRef(url);
  if (!projectRef) return null;

  const raw = window.localStorage.getItem(`sb-${projectRef}-auth-token`);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as SupabaseStoredSession | { currentSession?: SupabaseStoredSession };
    const session: SupabaseStoredSession | undefined =
      'currentSession' in (parsed as Record<string, unknown>)
        ? (parsed as { currentSession?: SupabaseStoredSession }).currentSession
        : (parsed as SupabaseStoredSession);
    if (!session) return null;
    if (typeof session.expires_at === 'number' && session.expires_at * 1000 <= Date.now()) {
      return null;
    }
    return typeof session.access_token === 'string' && session.access_token.length > 0
      ? session.access_token
      : null;
  } catch {
    return null;
  }
}

export async function authedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const token = readSupabaseAccessToken();
  if (!token) return fetch(input, init);
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return fetch(input, { ...init, headers });
}

function extractProjectRef(url: string): string | null {
  const match = /^https?:\/\/([a-z0-9]+)\.supabase\.co/i.exec(url.trim());
  return match?.[1] ?? null;
}

function readEnv(name: string): string | undefined {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.[name];
}
